export const environment = {
  production: true,
  environment: 'prod',
  /** Keep MSAL/B2C active in production until cutover; toggled per environment. */
  useBetterAuth: true,
  /** Keep native production on AAD until the native Better Auth cutover is enabled. */
  useBetterAuthNative: false,
  betterAuth: {
    baseUrl: 'https://auth.escrowtab.com',
    clientIdWeb: 'escrowtab-web',
    clientIdAdmin: 'escrowtab-admin',
    scope: 'openid profile email offline_access roles',
    /**
     * The `escrowtab` (web) app's own origin — NOT `window.location.origin`.
     * Password-reset emails (admin users + shared ForgotPasswordComponent) must
     * always send the end user to the product web app's `/auth/reset-password`.
     * Native Capacitor/Electron origins are custom schemes email clients cannot
     * open; admin's own origin would strand the user in the admin SPA.
     */
    webAppOrigin: 'https://webapp.escrowtab.com',
    /** Gates the passwordless email-OTP login UI. Ship dark in production until verified. */
    emailOtpEnabled: false,
    /**
     * Gates the 2FA verify step + enrollment UI after a successful sign-in when the
     * account has 2FA enrolled. See `docs/two-factor-auth-analysis.md`.
     */
    twoFactorEnabled: true,
    /**
     * Gates the TOTP-specific UI (authenticator-app login toggle + enrollment
     * fieldset in Profile Settings). Ship dark until Phase B/C are verified in
     * staging (see docs/tasks/2026-08-18-totp-2fa-spa-implementation-plan.md in
     * better-auth-standalong).
     */
    totpEnabled: false,
  },
  docio: {
    aadScopeAdmin: ['https://identity.escrowtab.com/docio/access_as_admin'],
    aadScope: ['https://identity.escrowtab.com/docio/access_as_user'],
    domain: 'https://docio.escrowtab.com',
  },
  apiServer: {
    domain: 'https://app.escrowtab.com/api/v1/client',
    aadScope: [
      'https://identity.escrowtab.com/auth/api1',
      'https://identity.escrowtab.com/auth/api2',
    ],
    token:
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJldF9kYXRhX2FsbCI6IjEiLCJuYmYiOjE2NDc1MTc3NDEsImV4cCI6NDc3MTY1NTM0MSwiaWF0IjoxNjQ3NTE3NzQxfQ.JrgSiYv9QMtTY1LQSbPzIzUxZNq-c3nN_TlrC82APP4',
  },
  apiDocPrep: {
    /** Mirror docPrepService.domain so IdTokenBearerInterceptor covers DocPrep calls (see §3.3 of the Better Auth guide). */
    domain: 'https://docprep-api.escrowtab.com',
    aadScope: ['https://identity.escrowtab.com/docprep/access_as_user'],
    aadScopeAdmin: ['https://identity.escrowtab.com/docprep/access_as_admin'],
  },
  apiOrders: {
    domain: 'https://orders-api.escrowtab.com/api',
    aadScope: ['https://identity.escrowtab.com/orders/Orders.Access'],
  },
  apiSecureLinks: {
    domain: 'https://links-api.escrowtab.com/api',
    aadScope: ['https://identity.escrowtab.com/links/User.Read'],
  },
  nnaScheduler: {
    domain: 'https://scheduler-stg.escrowtab.com/api/nna-scheduler',
  },
  auditService: {
    domain: 'https://auditv2-api.escrowtab.com/api',
    aadScope: ['https://identity.escrowtab.com/auditv2/access_as_user'],
  },
  adminAPI: {
    domain: 'https://admin-api.escrowtab.com/api',
    aadScope: ['https://identity.escrowtab.com/admin/access_as_user'],
  },
  googleAPIS: 'AIzaSyCr9L8QfLTwHpCsuJgFUyirgYib51zAkk0',
  sentryDns:
    'https://c2bc05c4cb3344d1af74580285d315df@o1214306.ingest.sentry.io/6377800',
  sentryNativeDns:
    'https://20582132274048b98c709d7b1fd3d279@o1214306.ingest.sentry.io/4503899375927296',
  sentryElsiDns:
    'https://824fc15c76452c4c6d39da7066216807@o1214306.ingest.sentry.io/4506583402086400',
  aad: {
    clientId: '6b2def53-b0cd-4f7f-acd3-aace65c6c28b',
    clientIdNative: '378ca7ba-b1cb-419b-a247-069ddca9ddfb',
    verifyClientId: '4867df42-68f1-4fe7-bed9-97a365f8b0d3',
    evaultClientId: '36aa10d3-ff41-4807-a56d-e48ed426814e',
    b2cPolicies: {
      names: {
        signIn: 'B2C_1_signin-01',
        editProfile: 'B2C_1_editprofile-01',
      },
      authorities: {
        signIn: {
          authority:
            'https://login.escrowtab.com/identity.escrowtab.com/B2C_1_signin-01',
        },
        editProfile: {
          authority:
            'https://login.escrowtab.com/identity.escrowtab.com/B2C_1_editprofile-01',
        },
      },
      authorityDomain: ['identity.escrowtab.com', 'login.escrowtab.com'],
    },
  },
  adminAAD: {
    clientId: 'e0e76bf8-9d85-4df6-a890-091ab415f3e9',
    b2cPolicies: {
      authorities: {
        signIn: {
          authority:
            'https://login.microsoftonline.com/36af3843-3860-422b-8cbe-52bc996630e9',
        },
      },
      authorityDomain: ['escrowtab.com'],
    },
  },
  packetService: {
    domain: 'https://packet-api.escrowtab.com',
    aadScope: ['https://identity.escrowtab.com/packet/access_as_user'],
    aadScopeAdmin: ['https://identity.escrowtab.com/packet/access_as_admin'],
  },
  docPrepService: {
    domain: 'https://docprep-api.escrowtab.com',
    domain_mono: 'https://docprep-mono-prod.azurewebsites.net',
    aadScope: ['https://identity.escrowtab.com/docprep/access_as_user'],
    aadScopeAdmin: ['https://identity.escrowtab.com/docprep/access_as_admin'],
  },
  profilev2: {
    domain: 'https://profilev2-api.escrowtab.com',
    aadScope: ['https://identity.escrowtab.com/profilev2/access_as_user'],
    aadScopeAdmin: ['https://identity.escrowtab.com/profilev2/access_as_admin'],
  },
  amplitude: 'a0dc2fdfc9ffd20fd872af2817825790',
  stripe: {
    publisherKey:
      'pk_live_51L6K9fJrriO4Nt49REbEG4nfcWd8NJTjyZXspshByBL3h02Rrmf5t6ZHK6bhXKZ33HkKhfDKlJQTEsYNTlOOjcgZ004XvGWjk9',
  },
  pusher: {
    apiKey: '6b3e2fbe5274df819cd9',
    cluster: 'mt1',
    channelAuthorizationEndpoint:
      'https://packet-api.escrowtab.com/api/Signer/pageNavigationAuth',
  },
  p2pConnectionService: {
    domain: 'wss://http-relay-poc.southcentralus.cloudapp.azure.com',
  },
};
